import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { insuranceData as staticInsuranceData } from '../data/insurance';
import { HeroSection } from '../components/insurance/HeroSection';
import { AcsSection } from '../components/insurance/AcsSection';
import { GeneralInfoSection } from '../components/insurance/GeneralInfoSection';
import { WhyChooseSection } from '../components/insurance/WhyChooseSection';
import { TestimonialsSection } from '../components/insurance/TestimonialsSection';
import { FaqSection } from '../components/insurance/FaqSection';
import { CtaSection } from '../components/insurance/CtaSection';
import { EmergencyInstructions } from '../components/insurance/EmergencyInstructions';
import { LegalFooter } from '../components/insurance/LegalFooter';
import { VideoSection } from '../components/insurance/VideoSection';
import { GuaranteesSection } from '../components/insurance/GuaranteesSection';
import { PricingSection } from '../components/insurance/PricingSection';
import * as amplitude from '@amplitude/analytics-browser';

export function InsurancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [insuranceData] = React.useState(staticInsuranceData);
  const [expandedFaqSection, setExpandedFaqSection] = React.useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] = React.useState<string | null>(null);
  const [selectedFaqCategory, setSelectedFaqCategory] = React.useState<'all' | 'general' | 'canada' | 'other' | 'card'>('all');
  const [showStickyBar, setShowStickyBar] = React.useState(false);

  React.useEffect(() => {
    amplitude.track("Insurance Displayed");
  }, []);

  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const validFaqSections = React.useMemo(() => {
    if (!insuranceData?.faq?.sections) return [];

    const sections = insuranceData.faq.sections
      .filter(section => {
        return section &&
               typeof section.title === 'string' &&
               Array.isArray(section.questions) &&
               section.questions.length > 0;
      })
      .map(section => ({
        ...section,
        questions: section.questions.filter(q =>
          q &&
          typeof q.question === 'string' &&
          typeof q.answer === 'string' &&
          q.question.trim() !== '' &&
          q.answer.trim() !== ''
        ),
        category: section.title === 'Questions pour toutes les destinations'
          ? 'general'
          : section.title === 'Questions spécifiques à la destination Canada'
            ? 'canada'
            : section.title === 'Question spécifique aux destinations hors Canada'
              ? 'other'
              : section.title === 'Carte d\'assuré pour le contrat Globe PVT'
                ? 'card'
                : 'general'
      }))
      .filter(section => section.questions.length > 0);

    if (selectedFaqCategory === 'all') {
      return sections;
    }
    return sections.filter(section => section.category === selectedFaqCategory);
  }, [insuranceData, selectedFaqCategory]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col pb-mobile overflow-x-hidden">
      <Navbar
        onLogin={() => {}}
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <main className="flex-1 pt-16">
        {insuranceData && (
          <>
            <HeroSection
              image={insuranceData.header.image}
              title={insuranceData.header.title}
              content={insuranceData.header.content}
            />

            <GeneralInfoSection />

            <PricingSection />

            <WhyChooseSection
              title={insuranceData.whyChoosePVT.title}
              content={insuranceData.whyChoosePVT.content}
              sections={insuranceData.whyChoosePVT.sections}
            />

            <GuaranteesSection />

            <TestimonialsSection />

            <VideoSection />

            <AcsSection
              title={insuranceData.ascHeader.title}
              content={insuranceData.ascHeader.content}
              image={insuranceData.intro.image}
            />

            <EmergencyInstructions />

            <FaqSection
              title={insuranceData.whyChoosePVT.title}
              content={insuranceData.whyChoosePVT.content}
              sections={validFaqSections}
              selectedCategory={selectedFaqCategory}
              onCategoryChange={setSelectedFaqCategory}
              expandedFaqSection={expandedFaqSection}
              setExpandedFaqSection={setExpandedFaqSection}
              expandedQuestion={expandedQuestion}
              setExpandedQuestion={setExpandedQuestion}
            />

            <CtaSection />
          </>
        )}

        <LegalFooter />
      </main>

      <Footer />

      <div
        className={`fixed bottom-[72px] left-0 right-0 z-40 lg:hidden transition-all duration-300 ${
          showStickyBar ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-t border-gray-200/50 dark:border-gray-700/50 px-4 py-2.5">
          <a
            href="https://www.acs-ami.com/fr/part/pvtistes/globe-pvt/?part=pvtistes&utm_source=pvtistes-pwa&utm_medium=aff-link&utm_campaign=gp-pvt-pwa"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => amplitude.track("Insurance CTA Clicked", { location: "sticky-bar" })}
            className="group flex items-center justify-center gap-2 w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 transition-all active:scale-[0.98]"
          >
            <span>Obtenir un devis gratuit</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
